/**
 * tutorCloseOutAdapter — le bilan de séance du masteur (`end-session-tutor`) : la clôture qualité
 * (`getQualityClosure`) et l'occurrence (`getLiveOccurrence`, ses rapports de live) → les formes de
 * `<TutorCloseOutPage>`.
 *
 * Les règles de l'ancien écran (`CloseOutScreen`, `StudentCloseOutCard`, `SessionCard`,
 * `LiveSatisfactionSection`) qui disent quoi montrer vivent ici, pas dans la page : les axes gardés
 * quand personne n'est venu, la productivité en attente du score oral (et « Productivité basse »
 * tue pendant ce temps), la polarité des étiquettes, le vocal arrivé vide. Le score d'engagement se
 * lit par l'adaptateur partagé du masteur (`toUITutorEngagement`). Jamais importé par le DS.
 */
import moment from "moment"
import "moment/locale/fr"

import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import {
    type UITutorCloseOutStudent,
    type UITutorCommentMode,
    type UITutorEngagement,
    type UITutorProductivityState,
    type UITutorQualityAlert,
    type UITutorQualityAxis,
} from "../../../ui/types/tutorCloseOut"
import { type ApiAxisScore } from "../../parent/LiveReports/liveReportDetailsAdapter"
import { type ApiEngagementScores, toUITutorEngagement } from "../shared/tutorEngagementAdapter"

// ── Les réponses du serveur ──────────────────────────────────────────────────────────────────

export type ApiQualityAlert = {
    indicator?: string | null
    charter_breach?: boolean | null
    charter_rule?: string | null
    recurrence_count?: number | null
    message?: string | null
}

export type ApiQualityAxis = {
    name?: string | null
    status?: string | null
    alerts?: ApiQualityAlert[] | null
}

/** Un élève de la clôture : ses axes, et ses scores à plat (sans détail). */
export type ApiQualityStudent = {
    student_id?: number | string | null
    total_score?: number | null
    scores?: { assiduity?: number | null; interaction?: number | null; productivity?: number | null } | null
    axes?: ApiQualityAxis[] | null
}

export type ApiQualityClosure = {
    tutor_proxies?: ApiQualityAxis[] | null
    students?: ApiQualityStudent[] | null
    no_student_attended?: boolean | null
    tutor_connection_minutes?: number | null
}

export type ApiCloseOutReport = {
    id: number | string
    student?: {
        id?: number | string | null
        display_name?: string | null
        firstname?: string | null
        avatar?: string | null
        profile?: { class?: string | null } | null
    } | null
    /** Absent : l'élève n'est pas venu. */
    statistics?: {
        scores?: ApiEngagementScores | null
        oral_productivity?: { oral_scoring_failed?: boolean | null } | null
    } | null
    tutor_rating?: number | null
    tutor_tags?: (string | { key?: string | null })[] | null
    tutor_comment?: string | null
    comment_record?: string | null
    comment_record_untranscribed?: boolean | null
}

export type ApiCloseOutOccurrence = {
    id?: number | string | null
    type?: string | null
    date?: string | null
    oral_scoring_enabled?: boolean | null
    live_reports?: ApiCloseOutReport[] | null
    tutor_live_quality_score?: number | null
    tutor_comment?: string | null
    live_satisfaction_tags?: string[] | null
    live_satisfaction_record?: string | null
}

/** Ce que la prescription du champion passe au bilan, par élève. */
export type ApiMissionPrescription = { activities_count?: number | null; activity_ids?: unknown[] | null }

/** Les étiquettes de `static-data` (`tutor_tags_*`, `live_satisfaction_tags_*`) : clé → libellé et émoji. */
export type ApiTagCatalog = Record<string, { label?: string | null; emoji?: string | null }>

// ── « Les bases » et les alertes ─────────────────────────────────────────────────────────────

/** Le libellé d'un indicateur sur sa chip — la table de l'ancien écran (`ALERT_LABELS`). */
const ALERT_LABELS: Record<string, string> = {
    camera: "Caméra",
    lateness: "Retard",
    voice_interaction: "Peu d'échanges",
    productivity_score: "Productivité basse",
    activities_submitted: "Aucune activité",
    max_silent: "Silence prolongé",
    hand_raise: "Main levée ignorée",
    out_of_app: "Sortie de l'app",
    reconnections: "Reconnexions",
    bad_quality: "Connexion instable",
    device: "Téléphone",
    objective_mismatch: "Objectif incohérent",
}

function toUITutorQualityAlert(alert: ApiQualityAlert): UITutorQualityAlert {
    const charterBreach = alert.charter_breach === true
    const recurrence = alert.recurrence_count ?? undefined
    return {
        label: charterBreach ? (alert.charter_rule ?? "Charte") : (ALERT_LABELS[alert.indicator ?? ""] ?? "Attention"),
        charterBreach,
        ...(recurrence != null && recurrence >= 2 ? { recurrenceCount: recurrence } : {}),
        message: alert.message ?? "",
    }
}

/** « OK » si le serveur le dit ; sinon « Alerte » dès un manquement à la charte, « Attention » dessous. */
export function toUITutorQualityAxis(axis: ApiQualityAxis): UITutorQualityAxis {
    const alerts = (axis.alerts ?? []).map(toUITutorQualityAlert)
    const status = axis.status === "ok" ? "ok" : alerts.some((alert) => alert.charterBreach) ? "alert" : "warning"
    return { name: axis.name ?? "", status, alerts: status === "ok" ? [] : alerts }
}

/**
 * Personne n'est venu : le serveur a déjà retiré les règles qui mesurent le masteur face à ses
 * élèves ; des axes restants, on ne garde que ceux qui ont encore une alerte (une connexion instable
 * reste vraie, des axes tout verts sur une séance vide ne jugeraient rien).
 */
export function toCloseOutProxies(closure: ApiQualityClosure | null): UITutorQualityAxis[] {
    const proxies = closure?.tutor_proxies ?? []
    const kept = closure?.no_student_attended ? proxies.filter((axis) => (axis.alerts?.length ?? 0) > 0) : proxies
    return kept.map(toUITutorQualityAxis)
}

export function noStudentAttendedOf(closure: ApiQualityClosure | null): { tutorConnectionMinutes: number } | null {
    if (!closure?.no_student_attended) return null
    return { tutorConnectionMinutes: closure.tutor_connection_minutes ?? 0 }
}

// ── Le score d'engagement ────────────────────────────────────────────────────────────────────

/**
 * La productivité se recalcule après la clôture (40 à 120 s) : tant que `oral_productivity` n'a pas
 * atterri, la valeur en base est le score de l'écrit seul (0 pour un élève qui n'a rien soumis).
 * Le job l'écrit même quand l'élève n'a presque pas parlé : son absence veut dire « pas encore
 * calculé », jamais « rien à dire ». `oral_scoring_failed` : il ne viendra plus.
 */
export function productivityStateOf(report: ApiCloseOutReport, oralScoringEnabled: boolean): UITutorProductivityState {
    if (report.statistics?.oral_productivity?.oral_scoring_failed) return "failed"
    if (oralScoringEnabled && report.statistics != null && !report.statistics.oral_productivity) return "pending"
    return "ready"
}

/** Un score oral manque encore : l'écran relit (le score arrive après la clôture). */
export function awaitsOralScore(occurrence: ApiCloseOutOccurrence | null): boolean {
    if (!occurrence?.oral_scoring_enabled) return false
    return (occurrence.live_reports ?? []).some(
        (report) => report.statistics != null && !report.statistics.oral_productivity
    )
}

const flatAxis = (value: number | null | undefined): ApiAxisScore | undefined =>
    value == null ? undefined : { score: value }

/**
 * Le détail vient du rapport (`statistics.scores`) ; le total de la clôture quand elle en a un. Un
 * élève sans scores sur son rapport garde la barre de la clôture, à plat.
 */
export function closeOutEngagementOf(
    report: ApiCloseOutReport,
    qualityStudent: ApiQualityStudent | undefined,
    productivity: UITutorProductivityState
): UITutorEngagement | null {
    const reportScores = report.statistics?.scores ?? null
    const total = qualityStudent?.total_score ?? reportScores?.total_score ?? null
    if (reportScores != null) return toUITutorEngagement({ ...reportScores, total_score: total }, productivity)
    if (qualityStudent?.total_score == null) return null
    const flat = qualityStudent.scores ?? {}
    return toUITutorEngagement(
        {
            total_score: total,
            assiduity: flatAxis(flat.assiduity),
            interaction: flatAxis(flat.interaction),
            productivity: flatAxis(flat.productivity),
        },
        productivity
    )
}

/**
 * Les axes de l'élève qui ne sont pas OK. Pendant l'attente du score oral, « Productivité basse »
 * se tait : le serveur la lève sur le score de l'écrit seul, elle tombera d'elle-même. « Aucune
 * activité » reste : elle constate un fait indépendant du score oral.
 */
export function studentAlertAxesOf(
    qualityStudent: ApiQualityStudent | undefined,
    productivity: UITutorProductivityState
): UITutorQualityAxis[] {
    return (qualityStudent?.axes ?? [])
        .map((axis) => {
            if (productivity !== "pending") return axis
            const alerts = (axis.alerts ?? []).filter((alert) => alert.indicator !== "productivity_score")
            return alerts.length === (axis.alerts ?? []).length
                ? axis
                : { ...axis, alerts, status: alerts.length > 0 ? axis.status : "ok" }
        })
        .filter((axis) => axis.status !== "ok")
        .map(toUITutorQualityAxis)
}

// ── Le compte-rendu ──────────────────────────────────────────────────────────────────────────

/** Le vocal du masteur, sauf s'il est arrivé vide (inaudible) : il ne dit rien aux parents. */
export function usableCommentRecord(report: ApiCloseOutReport): string | null {
    return report.comment_record_untranscribed ? null : (report.comment_record ?? null)
}

/** Fait côté serveur : une note, et un commentaire écrit ou un vocal utilisable. */
export function isCloseOutDone(report: ApiCloseOutReport): boolean {
    if (report.statistics == null || !((report.tutor_rating ?? 0) > 0)) return false
    return (report.tutor_comment ?? "").trim() !== "" || usableCommentRecord(report) != null
}

/** Les étiquettes d'un rapport : des clés, ou des objets qui portent la leur. */
export function tagKeysOf(tags: ApiCloseOutReport["tutor_tags"]): string[] {
    if (!Array.isArray(tags)) return []
    return tags
        .map((tag) => (typeof tag === "object" && tag != null ? (tag.key ?? "") : String(tag)))
        .filter((key) => key !== "")
}

/** Un mot écrit ouvre le champ texte ; sinon, le vocal (l'ancien écran). */
export function initialCommentMode(text: string | null | undefined): UITutorCommentMode {
    return (text ?? "").trim() !== "" ? "text" : "voice"
}

// ── Les étiquettes ───────────────────────────────────────────────────────────────────────────

export type TagChoice = { key: string; label: string }

/** « 🙌 Bonne participation » : une étiquette telle qu'une page la montre (le bilan, l'historique). */
export function tagLabelOf(tag: { emoji?: string | null; label?: string | null } | null | undefined): string {
    return [tag?.emoji, tag?.label].filter((part) => part != null && part !== "").join(" ")
}

/** Le libellé de chaque étiquette du catalogue, et sa clé. */
export function toTagChoices(catalog: ApiTagCatalog | null | undefined): TagChoice[] {
    return Object.entries(catalog ?? {}).map(([key, tag]) => ({ key, label: tagLabelOf(tag) }))
}

/** Les étiquettes d'un élève : les positives à partir de 4 étoiles. */
export const studentTagsPositive = (rating: number) => rating >= 4

/** Les étiquettes de la plateforme : les positives à 5 étoiles seulement. */
export const platformTagsPositive = (rating: number) => rating === 5

export function studentTagsTitle(rating: number): string | undefined {
    if (rating <= 0) return undefined
    return studentTagsPositive(rating) ? "Qu'est-ce que tu as apprécié le plus ?" : "Quelles ont été les difficultés ?"
}

// ── L'en-tête et les élèves ──────────────────────────────────────────────────────────────────

/** « MARDI 27 OCTOBRE 17:00 » — l'ancien écran l'écrit en capitales. */
export function closeOutDateLabel(date: string | null | undefined): string | undefined {
    if (date == null || date === "") return undefined
    const parsed = moment(date).locale("fr")
    return parsed.isValid() ? parsed.format("dddd DD MMMM HH:mm").toUpperCase() : undefined
}

export function isTrialOccurrence(occurrence: ApiCloseOutOccurrence | null): boolean {
    return occurrence?.type === "trial"
}

/** La ligne du travail prescrit (« Travail … : N exercices envoyés ») — rien quand rien n'a été prescrit. */
export function samActivitiesCountOf(prescription: ApiMissionPrescription | null | undefined): number | undefined {
    if (prescription == null) return undefined
    return prescription.activities_count ?? prescription.activity_ids?.length ?? 0
}

/** L'identité d'un élève sur sa carte, absent compris. */
export function closeOutStudentIdentity(
    report: ApiCloseOutReport
): Pick<UITutorCloseOutStudent, "id" | "firstname" | "className" | "avatarUrl"> {
    const student = report.student
    const avatar = student?.avatar ? (cleanMediaUrl(student.avatar) as string | null) : null
    return {
        id: report.id,
        firstname: student?.display_name ?? student?.firstname ?? "l'élève",
        ...(student?.profile?.class ? { className: student.profile.class } : {}),
        ...(avatar ? { avatarUrl: avatar } : {}),
    }
}

export function qualityStudentsById(closure: ApiQualityClosure | null): Map<string, ApiQualityStudent> {
    const byId = new Map<string, ApiQualityStudent>()
    for (const student of closure?.students ?? []) {
        if (student.student_id != null) byId.set(String(student.student_id), student)
    }
    return byId
}

// ── Les réponses d'écriture ──────────────────────────────────────────────────────────────────

export const VOICE_NOT_SENT = "Ton vocal n'a pas été envoyé. Il est toujours là : réessaie."
export const TEXT_NOT_SAVED = "Ton commentaire n'a pas été enregistré. Il est toujours là : réessaie."

/**
 * Un refus (422) porte la phrase à montrer sur son champ — son `message` est le générique de
 * Laravel ; tout le reste (500, réseau, l'envoi au stockage) prend la nôtre.
 */
export function closeOutSendError(failure: unknown, fallback: string): string {
    const error = failure as { status?: number; errors?: Record<string, string[] | undefined> } | null
    if (error?.status !== 422) return fallback
    const first = Object.values(error.errors ?? {})[0]?.[0]
    return first != null && first !== "" ? first : fallback
}
